import { useState, useEffect } from 'react';
import { confirmDisconnect } from './disconnect-confirm.store';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useIntegrations, useDisconnectIntegration } from '@/hooks';
import { integrationService } from '@/services';
import type {
	GladlyUser,
	GladlyStepCredentials
} from '@/services/integration.service';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Radio, MoreHorizontal, Info, Check, UserCheck, Loader2, Copy } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import type { Integration } from '../types';

// ---------------------------------------------------------------------------
// Schema & helpers
// ---------------------------------------------------------------------------

const gladlyFormSchema = z.object({
	organization: z.string().min(1, 'Organization name is required').max(256, 'Organization name is too long'),
	agentEmail: z.string().email('Enter a valid agent email').max(254, 'Email is too long'),
	apiKey: z.string().min(1, 'API token is required').max(512, 'API token is too long')
});

type GladlyFormData = z.infer<typeof gladlyFormSchema>;

// ---------------------------------------------------------------------------
// Step definitions
// ---------------------------------------------------------------------------

/**
 * Machine registers the Gladly webhooks itself during the final connect call, so there is no
 * manual webhook step: nothing for the customer to copy, and no HTTP Target to create by hand.
 */
const GLADLY_STEPS = ['API Key', 'Reply User', 'Finish'];

/**
 * The agent address Kim suggests creating for automated replies.
 *
 * Advisory only. Kim used to look this up on the roster and adopt it automatically, which is
 * how a DEACTIVATED agent ended up authoring replies — Gladly's Agents API exposes no status
 * field anywhere, so an adopted agent could be one Gladly will not let post. Now it is just a
 * recommended address to create; the operator still picks the agent themselves.
 */
const SUGGESTED_AGENT_EMAIL = 'connect@kim.cc';

/** Wizard step indices, named so the effects and gating below don't hinge on bare numbers. */
const STEP = {
	API_KEY: 0,
	REPLY_USER: 1,
	FINISH: 2
} as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface GladlyContentProps {
	integration: Integration;
	onOpenChange?: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Gated helper: returns true when the user has filled the three credential fields
// ---------------------------------------------------------------------------

const hasCredentials = (organization: string, agentEmail: string, apiKey: string) =>
	!!(organization.trim() && agentEmail.trim() && apiKey.trim());

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GladlyContent = ({ integration, onOpenChange }: GladlyContentProps) => {
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const connected = integration.isConnected;

	const form = useForm<GladlyFormData>({
		resolver: zodResolver(gladlyFormSchema),
		defaultValues: { organization: '', agentEmail: '', apiKey: '' }
	});
	const organization = form.watch('organization');
	const agentEmail = form.watch('agentEmail');
	const apiKey = form.watch('apiKey');

	const [users, setUsers] = useState<GladlyUser[] | null>(null);
	const [isLoadingUsers, setIsLoadingUsers] = useState(false);
	const [usersError, setUsersError] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState('');
	const [isConnecting, setIsConnecting] = useState(false);
	/** The chosen agent, for the summary on the final step. */
	const selectedUser = (users ?? []).find((u) => u.id === selectedUserId) ?? null;
	const [step, setStep] = useState(0);

	const credsReady = hasCredentials(organization, agentEmail, apiKey);

	/** The credential triple every stateless wizard step posts to machine. */
	const stepCredentials = (): GladlyStepCredentials => ({
		organization,
		agent_email: agentEmail,
		api_token: apiKey
	});

	/** Surface the message machine sent — it names the missing Gladly access on a 401/403. */
	const errorMessage = (err: unknown, fallback: string) => {
		const body = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
		return body?.error ?? body?.message ?? fallback;
	};

	// --- Load users when the Reply User step is opened ---
	useEffect(() => {
		if (step === STEP.REPLY_USER && users === null && !isLoadingUsers && !usersError && credsReady) {
			void reloadUsers();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [step]);

	const reloadUsers = async () => {
		if (!credsReady) return;
		setIsLoadingUsers(true);
		setUsersError(false);
		try {
			const r = await integrationService.listGladlyUsers(stepCredentials());
			setUsers(r.users);
			// No agent is pre-selected. Kim used to auto-adopt its own `gladly-bot@kim.cc`, but
			// Gladly's Agents API exposes no status field on ANY endpoint (verified live), so a
			// deactivated agent is indistinguishable from an active one — and auto-adopting a
			// deactivated agent bound replies to an author Gladly will not let post. The operator
			// picks, because only they can see which agents are actually active in Gladly.
		} catch (err) {
			setUsersError(true);
			toast.error(errorMessage(err, 'Could not reach Gladly to load users.'));
		} finally {
			setIsLoadingUsers(false);
		}
	};

	// --- Finish ---
	// Machine registers the Gladly webhooks as part of this call and fails the connect if
	// Gladly rejects them, so an error here can mean provisioning rather than persistence.
	const handleFinish = async () => {
		setIsConnecting(true);
		try {
			await integrationService.connectGladlyIntegration({
				...stepCredentials(),
				agent_uuid: selectedUserId || undefined
			});
			await refetch();
			onOpenChange?.(false);
		} catch (err) {
			toast.error(errorMessage(err, 'Failed to connect Gladly'));
		} finally {
			setIsConnecting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	// =========================================================================
	// Connected view
	// =========================================================================
	if (connected) {
		const savedOrg = (integration.authMetaData?.organization as string | undefined) ?? '';
		const savedWebhookIds = (integration.authMetaData?.webhook_ids as Record<string, string> | undefined) ?? {};
		const webhooksRegistered = Object.keys(savedWebhookIds).length > 0;
		return (
			<div className='flex flex-col gap-5 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected Gladly</h3>
						<p className='text-[10px] italic text-muted-foreground'>
							Your Gladly workspace is linked to Kim.
						</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem
								variant='destructive'
								onClick={handleDisconnect}
								disabled={disconnectMutation.isPending}
							>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<Separator />
				<div className='flex flex-col gap-3'>
					<span className='text-sm font-semibold'>Webhook</span>
					{webhooksRegistered ? (
						<div className='flex items-center gap-2 text-xs text-green-700'>
							<Check className='size-4 text-green-600' />
							Kim registered {Object.keys(savedWebhookIds).length} webhooks in Gladly. No manual setup
							needed — they are removed automatically when you disconnect.
						</div>
					) : (
						<div className='flex gap-2 rounded-md bg-amber-50 border border-amber-200 p-2.5'>
							<Info className='size-4 shrink-0 text-amber-600 mt-0.5' />
							<p className='text-xs text-amber-700 leading-snug'>
								No webhooks are registered for this connection. Reconnect to let Kim provision them.
							</p>
						</div>
					)}
					{savedOrg && (
						<div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
							<Label className='text-xs text-muted-foreground'>Organization</Label>
							<span className='text-sm font-medium'>{savedOrg}</span>
						</div>
					)}
				</div>
			</div>
		);
	}

	// =========================================================================
	// Wizard steps
	// =========================================================================

	const stepper = (
		<div className='flex items-start justify-between px-5'>
			{GLADLY_STEPS.map((label, i) => {
				const state = i < step ? 'done' : i === step ? 'active' : 'todo';
				const circle =
					state === 'done'
						? 'bg-green-50 border-green-300 text-green-700'
						: state === 'active'
							? 'bg-foreground text-background border-foreground'
							: 'bg-muted text-muted-foreground border-border';
				return (
					<div key={label} className='relative flex flex-1 flex-col items-center'>
						{i > 0 && (
							<div
								className={`absolute top-3.5 right-1/2 h-px w-full ${i <= step ? 'bg-green-300' : 'bg-border'}`}
							/>
						)}
						<button
							type='button'
							onClick={() => {
								if (i <= step) setStep(i);
							}}
							disabled={i > step}
							className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full border text-[11px] ${circle} ${i > step ? 'cursor-default' : 'cursor-pointer'}`}
						>
							{state === 'done' ? <Check className='size-3.5' /> : i + 1}
						</button>
						<span
							className={`mt-1 text-[10px] ${state === 'todo' ? 'text-muted-foreground' : 'text-foreground'}`}
						>
							{label}
						</span>
					</div>
				);
			})}
		</div>
	);

	// --- Step 0: API Key ---
	const apiKeyStep = (
		<div className='flex flex-col gap-3'>
			<h3 className='text-sm font-semibold'>Add your Gladly credentials</h3>
			<p className='text-xs text-muted-foreground'>
				Enter your Gladly organization name, the email of an agent with API access, and that agent's API
				token. Nothing is saved until you finish the wizard.
			</p>
			<FormField
				control={form.control}
				name='organization'
				render={({ field }) => (
					<FormItem>
						<FormLabel>Organization Name</FormLabel>
						<FormControl>
							<Input placeholder='your-org' {...field} />
						</FormControl>
						<p className='text-xs text-muted-foreground'>
							Your Gladly organization slug (e.g. your-org from your-org.gladly.com).
						</p>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				control={form.control}
				name='agentEmail'
				render={({ field }) => (
					<FormItem>
						<FormLabel>Agent Email</FormLabel>
						<FormControl>
							<Input type='email' placeholder='agent@yourcompany.com' {...field} />
						</FormControl>
						<p className='text-xs text-muted-foreground'>
							Email of a Gladly agent that has API access.
						</p>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				control={form.control}
				name='apiKey'
				render={({ field }) => (
					<FormItem>
						<FormLabel>API Token</FormLabel>
						<FormControl>
							<Input
								type='password'
								autoComplete='new-password'
								placeholder='Enter your Gladly API token'
								{...field}
							/>
						</FormControl>
						<p className='text-xs text-muted-foreground'>
							Found in Gladly under Settings → API Tokens.
						</p>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	);


	/** Copy the suggested agent address — it must be typed exactly when created in Gladly. */
	const copySuggestedEmail = async () => {
		try {
			await navigator.clipboard.writeText(SUGGESTED_AGENT_EMAIL);
			toast.success('Agent email copied');
		} catch {
			toast.error('Could not copy the agent email');
		}
	};

	// --- Step 1: Reply User ---
	// The operator picks any agent on the org; Kim no longer nominates one for them.
	//
	// This step used to auto-adopt `gladly-bot@kim.cc` whenever it existed and hide the picker
	// entirely. That was wrong in a way Kim cannot detect: Gladly's Agents API returns only
	// {id, name, emailAddress} — no status field on GET /agents, on GET /agents/{id}, or behind
	// any query parameter (verified live 2026-08-11) — so a DEACTIVATED agent is byte-identical
	// to an active one. Auto-adopting one bound every automated reply to an author Gladly will
	// not let post, and it surfaced at reply time rather than at connect. Only the operator can
	// see which agents are actually active, so the choice is theirs.
	const replyUserStep = (
		<div className='flex flex-col gap-3'>
			<h3 className='text-sm font-semibold'>Reply author</h3>
			{isLoadingUsers ? (
				<div className='flex items-center gap-2 text-xs text-muted-foreground'>
					<Loader2 className='size-4 animate-spin' />
					Loading agents&hellip;
				</div>
			) : usersError ? (
				<Button type='button' variant='outline' size='sm' className='self-start' onClick={reloadUsers}>
					Retry loading users
				</Button>
			) : (
				<>
					<p className='text-xs text-muted-foreground'>
						Choose the Gladly agent automated replies post as. It must be active in Gladly.
					</p>
					{/*
					 * A recommendation, not a gate — the picker below works with or without it.
					 * The address is copyable because it has to be typed exactly in Gladly for the
					 * agent to be recognisable here afterwards.
					 */}
					<div className='flex flex-col gap-2 rounded-md bg-blue-50 border border-blue-200 p-2.5'>
						<div className='flex gap-2'>
							<Info className='size-4 shrink-0 text-blue-600 mt-0.5' />
							<p className='text-xs text-blue-800 leading-snug'>
								We recommend a dedicated agent, so replies aren&rsquo;t attributed to one of
								your teammates. Create one in Gladly (Settings &rarr; Agents), then reload.
							</p>
						</div>
						<div className='flex items-center gap-1.5 pl-6'>
							<code className='min-w-0 flex-1 overflow-auto whitespace-nowrap rounded-md border border-blue-200 bg-white p-2 font-mono text-xs text-neutral-800'>
								{SUGGESTED_AGENT_EMAIL}
							</code>
							<Button
								type='button'
								size='icon-sm'
								variant='ghost'
								title='Copy agent email'
								onClick={() => void copySuggestedEmail()}
							>
								<Copy className='size-4' />
							</Button>
						</div>
					</div>
					<Select value={selectedUserId} onValueChange={setSelectedUserId}>
						<SelectTrigger>
							<SelectValue placeholder='Select a Gladly agent' />
						</SelectTrigger>
						<SelectContent>
							{(users ?? []).map((u) => (
								<SelectItem key={u.id} value={u.id}>
									{u.name ?? u.email ?? u.id}
									{u.name && u.email ? ` \u00b7 ${u.email}` : ''}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{/* Agents added or reactivated in Gladly since this step opened need a re-fetch;
					    the roster is loaded once when the step is first shown. */}
					<Button type='button' variant='outline' size='sm' className='self-start' onClick={reloadUsers}>
						Reload agents
					</Button>
					{selectedUserId && (
						<div className='flex items-center gap-2 text-xs text-green-700'>
							<UserCheck className='size-4 text-green-600' />
							Reply author set.
						</div>
					)}
				</>
			)}
		</div>
	);


	// --- Step 2: Done ---
	const doneStep = (
		<div className='flex flex-col items-center gap-2 pt-2 text-center'>
			<div className='flex h-12 w-12 items-center justify-center rounded-full bg-muted text-foreground'>
				<Radio className='size-6' />
			</div>
			<h3 className='text-sm font-semibold'>Ready to connect</h3>
			<p className='text-xs text-muted-foreground'>
				Click Finish to save your Gladly connection.
			</p>
			<div className='mt-2 grid w-full grid-cols-1 gap-1.5 text-left'>
				{[
					{ label: 'Credentials entered', ok: credsReady },
					{
						label: selectedUser
							? `Reply author: ${selectedUser.email ?? selectedUser.name ?? selectedUser.id}`
							: 'Reply author selected',
						ok: !!selectedUserId
					},
					// Provisioned server-side by the connect call, so these are stated, not checked.
					{ label: 'Tags created on finish', ok: true },
					{ label: 'Webhooks registered on finish', ok: true }
				].map((row) => (
					<div key={row.label} className='flex items-center gap-2 text-xs'>
						{row.ok ? (
							<Check className='size-4 text-green-600' />
						) : (
							<Info className='size-4 text-amber-600' />
						)}
						<span className={row.ok ? 'text-foreground' : 'text-muted-foreground'}>
							{row.label}
						</span>
					</div>
				))}
			</div>
		</div>
	);

	const stepContent = [apiKeyStep, replyUserStep, doneStep][step];
	const canAdvance = step === STEP.API_KEY ? credsReady : !!selectedUserId;
	const isLast = step === GLADLY_STEPS.length - 1;

	return (
		<Form {...form}>
			<div className='flex flex-col h-full'>
				{stepper}
				<Separator className='mt-3' />
				<div className='flex-1 overflow-auto px-5 py-4'>{stepContent}</div>
				<div className='px-5 pb-5 pt-4 flex-row flex items-center justify-between'>
					<Button
						type='button'
						variant='outline'
						onClick={() => setStep((s) => Math.max(0, s - 1))}
						disabled={step === STEP.API_KEY}
					>
						Back
					</Button>
					{isLast ? (
						<Button type='button' onClick={handleFinish} disabled={isConnecting}>
							<Radio className='size-4' />
							{isConnecting ? 'Connecting…' : 'Finish'}
						</Button>
					) : (
						<Button
							type='button'
							onClick={() => setStep((s) => Math.min(GLADLY_STEPS.length - 1, s + 1))}
							disabled={!canAdvance}
						>
							Next
						</Button>
					)}
				</div>
			</div>
		</Form>
	);
};
